import { zodResolver } from '@hookform/resolvers/zod';
import {
  buildAnswersSchema,
  fieldsByScope,
  flattenFields,
  isFieldVisible,
  type FormConfig,
  type FormField,
} from '@nexofacil/schemas';
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Sparkles,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { normalizeBrazilianPhone } from '@nexofacil/utils/client';
import { z } from 'zod';
import { StepsProgress } from '@/components/form/steps-progress';
import { Button } from '@/components/ui/button';
import { brokerAvatarClass, type AvatarShape } from '@/lib/broker-avatar';
import { DynamicField, fieldPath } from './dynamic-field';

// =============================================================================
// DynamicForm · multi-step form rendered from a `FormConfig`. Mirrors the
// MCMV form's chrome (sticky header with progress, sticky footer with back/next,
// honeypot, draft persistence via prop) but the body is fully driven by the
// config.
//
// Caller is responsible for the network call: pass an `onSubmit` that receives
// the cleanly-shaped payload `{ lead, answers }` and resolves when done.
// =============================================================================

export type DynamicFormPayload = {
  lead: Record<string, unknown>;
  answers: Record<string, unknown>;
};

export type DynamicFormProps = {
  config: FormConfig;
  /** Pre-fill from a draft / partial state. */
  initialValues?: Partial<DynamicFormPayload>;
  onSubmit: (payload: DynamicFormPayload) => Promise<void> | void;
  onClose?: () => void;
  /** Called on every state change so callers can persist a draft. */
  onChange?: (state: DynamicFormPayload, currentStepIndex: number) => void;
  /** Resumes on a given step (0-based). */
  initialStepIndex?: number;
  submitError?: string | null;
  /** Marca do corretor no cabeçalho; sem ela, cai na marca NexoFácil. */
  brand?: { name: string; logoUrl: string | null; avatarShape?: AvatarShape } | null;
};

type FormShape = Record<string, unknown> & { answers: Record<string, unknown> };

export function DynamicForm({
  config,
  initialValues,
  onSubmit,
  onClose,
  onChange,
  initialStepIndex,
  submitError,
  brand,
}: DynamicFormProps) {
  const [stepIndex, setStepIndex] = useState(initialStepIndex ?? 0);
  const [submitting, setSubmitting] = useState(false);
  // Banner mostrado abaixo dos campos quando o submit é bloqueado por
  // validação · evita o caso "clico no botão e nada acontece" que rola
  // quando o erro está num campo de step anterior fora da tela.
  const [validationBlocked, setValidationBlocked] = useState(false);
  const totalSteps = config.steps.length;

  // -------------------------------------------------------------------------
  // Build a runtime Zod schema that mirrors the config. Lead-scope fields go
  // on the top level (name/email/phone), answers-scope fields nest under
  // `answers`, plus a tolerant honeypot string.
  // -------------------------------------------------------------------------
  const schema = useMemo(() => {
    const answersSchema = buildAnswersSchema(config);
    const leadFields = fieldsByScope(config, 'lead');
    const leadShape: Record<string, z.ZodTypeAny> = {};
    for (const f of leadFields) {
      // Use the same Zod constraints buildLeadFieldsSchema would, but inline
      // so we avoid importing the helper just for one map.
      leadShape[f.id] = buildSingleFieldSchema(f);
    }
    return z
      .object({
        ...leadShape,
        answers: answersSchema,
        _honeypot: z.string().optional(),
      })
      .passthrough();
  }, [config]);

  const defaultValues = useMemo<FormShape>(() => {
    const base: FormShape = { answers: {}, _honeypot: '' };
    if (initialValues?.lead) Object.assign(base, initialValues.lead);
    if (initialValues?.answers) base.answers = { ...initialValues.answers };

    // Stepper fields always render a number visually (the counter widget has
    // no "empty" state), so the user perceives the displayed minimum (usually
    // 0) as their answer even if they never clicked +/-. Without seeding the
    // form state to match, the next-step validation marks the field as
    // missing and shows "Campo obrigatório: …" even though the UI looks
    // answered. Initialize stepper fields to their declared min (default 0).
    for (const f of flattenFields(config)) {
      if (f.type !== 'stepper') continue;
      const scope = f.scope ?? 'answers';
      const bucket = scope === 'lead' ? (base as Record<string, unknown>) : base.answers;
      if (bucket[f.id] === undefined) {
        bucket[f.id] = f.validation?.min ?? 0;
      }
    }
    return base;
  }, [initialValues, config]);

  const form = useForm<FormShape>({
    // zodResolver is permissive on type params; cast through unknown to keep
    // the form generic without a giant intersection type.
    resolver: zodResolver(schema as unknown as z.ZodType<FormShape>),
    mode: 'onTouched',
    defaultValues,
  });

  const { watch, trigger, getValues } = form;

  // Emit changes so the page can persist a draft.
  useEffect(() => {
    if (!onChange) return;
    const sub = watch((value) => {
      const v = value as FormShape;
      onChange(
        { lead: stripAnswers(v), answers: { ...(v.answers ?? {}) } },
        stepIndex,
      );
    });
    return () => sub.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onChange, stepIndex]);

  // Watched state for visibility checks.
  const liveLead = watch();
  const liveAnswers = (liveLead.answers as Record<string, unknown>) ?? {};

  // Current step + visible fields.
  const currentStep = config.steps[stepIndex]!;
  const visibleFields = currentStep.fields.filter((f) => {
    // Visibility looks up by field id on a unified state: lead-scope and
    // answers-scope keys are merged so a condition can reference either.
    const merged = { ...liveLead, ...liveAnswers };
    return isFieldVisible(f, merged as Record<string, unknown>);
  });

  async function goNext() {
    // Trigger only the visible fields on this step.
    const paths = visibleFields.map(fieldPath);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ok = await trigger(paths as any);
    if (ok) setStepIndex((s) => Math.min(totalSteps - 1, s + 1));
  }

  function goBack() {
    setStepIndex((s) => Math.max(0, s - 1));
  }

  async function handleSubmit(e?: FormEvent) {
    if (e) e.preventDefault();

    // Limpa valores residuais de campos CONDICIONAIS não visíveis antes de
    // validar. Caso clássico: user seleciona "Outra cidade", digita "X" (1
    // char), depois muda pra "São José". O cityOther some da tela mas o
    // valor "X" permanece no state e o schema (min 2 chars) bloqueia o
    // submit · usuário fica refém porque não consegue editar o campo.
    const liveValues = form.getValues();
    const liveMerged = {
      ...(liveValues as Record<string, unknown>),
      ...((liveValues.answers as Record<string, unknown>) ?? {}),
    };
    for (const f of flattenFields(config)) {
      if (!f.condition) continue;
      if (isFieldVisible(f, liveMerged)) continue;
      const path = fieldPath(f);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      form.setValue(path as any, undefined as never, { shouldValidate: false });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      form.clearErrors(path as any);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ok = await trigger(undefined as any);
    if (!ok) {
      // Achei o caso "botão não funciona": validação silenciosa em campo de
      // step anterior. Log no console pro debug + navega pra primeira step
      // com erro + mostra banner pro usuário entender que falta algo.
      const errors = form.formState.errors;
      // eslint-disable-next-line no-console
      console.warn('[DynamicForm] submit bloqueado por validação:', JSON.parse(JSON.stringify(errors)));
      const firstStepWithError = findFirstStepWithError(errors, config);
      if (firstStepWithError !== -1 && firstStepWithError !== stepIndex) {
        setStepIndex(firstStepWithError);
      }
      setValidationBlocked(true);
      return;
    }
    setValidationBlocked(false);
    setSubmitting(true);
    try {
      const values = getValues();
      // Drop hidden conditional fields from the payload so the qualifier sees
      // a clean answers object (matches what the user actually answered).
      const allFields = flattenFields(config);
      const merged = { ...(values as Record<string, unknown>), ...(values.answers ?? {}) };
      const lead = stripAnswers(values);
      const answers: Record<string, unknown> = {};
      for (const f of allFields) {
        const scope = f.scope ?? 'answers';
        if (scope !== 'answers') continue;
        if (!isFieldVisible(f, merged)) continue;
        const v = (values.answers as Record<string, unknown>)[f.id];
        if (v !== undefined) answers[f.id] = v;
      }
      await onSubmit({ lead, answers });
    } finally {
      setSubmitting(false);
    }
  }

  const isLastStep = stepIndex === totalSteps - 1;

  return (
    <div className="flex min-h-screen flex-col bg-secondary/30">
      <Header
        currentStep={stepIndex + 1}
        totalSteps={totalSteps}
        onClose={onClose}
        brand={brand}
      />

      <main className="flex-1">
        <form
          onSubmit={handleSubmit}
          className="container mx-auto max-w-xl px-4 pb-32 pt-8 md:pb-12 md:pt-12"
        >
          <input
            type="text"
            {...form.register('_honeypot')}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: '-10000px',
              top: 'auto',
              width: '1px',
              height: '1px',
              overflow: 'hidden',
            }}
          />

          <div key={stepIndex} className="animate-fade-in">
            <h1 className="text-balance text-2xl font-bold leading-tight md:text-3xl">
              {currentStep.title}
            </h1>
            {currentStep.subtitle && (
              <p className="mt-2 text-balance text-sm text-muted-foreground md:text-base">
                {currentStep.subtitle}
              </p>
            )}

            <div className="mt-8 space-y-7">
              {visibleFields.map((f) => (
                <DynamicFieldRow key={f.id} field={f} form={form} />
              ))}
            </div>
          </div>

          {validationBlocked && (
            <div className="mt-6 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
              Tem campo faltando preencher pra enviar · revise os passos
              acima.
            </div>
          )}
          {submitError && (
            <p className="mt-6 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {submitError}
            </p>
          )}
        </form>
      </main>

      <Footer
        isFirstStep={stepIndex === 0}
        isLastStep={isLastStep}
        submitting={submitting}
        submitLabel={config.submitLabel ?? 'Enviar respostas'}
        onBack={goBack}
        onNext={goNext}
        onSubmit={handleSubmit}
      />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Sub-pieces
// -----------------------------------------------------------------------------

function DynamicFieldRow({ field, form }: { field: FormField; form: ReturnType<typeof useForm<FormShape>> }) {
  const path = fieldPath(field);
  // For checkbox-ish values we need to keep watch() so the renderer reflects
  // the current selection (react-hook-form's register alone won't re-render).
  const value = form.watch(path);
  const error = readError(form.formState.errors, path);

  return (
    <DynamicField
      field={field}
      form={form as unknown as ReturnType<typeof useForm<Record<string, unknown>>>}
      value={value}
      setValue={(next) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        form.setValue(path as any, next as never, { shouldValidate: true });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        form.clearErrors(path as any);
      }}
      error={typeof error === 'string' ? error : undefined}
    />
  );
}

function Header({
  currentStep,
  totalSteps,
  onClose,
  brand,
}: {
  currentStep: number;
  totalSteps: number;
  onClose?: () => void;
  brand?: DynamicFormProps['brand'];
}) {
  return (
    <header className="sticky top-0 z-30 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="container flex h-16 items-center gap-3">
        {brand ? (
          <div className="flex min-w-0 items-center gap-2.5">
            {brand.logoUrl && (
              <img
                src={brand.logoUrl}
                alt=""
                className={brokerAvatarClass(brand.avatarShape, 'h-8 w-8 shrink-0')}
              />
            )}
            <span className="truncate text-sm font-semibold">{brand.name}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <img src="/nexofacil-icon.png" alt="" className="h-7 w-7" />
            <span className="hidden text-sm font-semibold sm:inline">NexoFácil</span>
          </div>
        )}
        <div className="ml-auto text-sm font-medium text-muted-foreground">
          Passo {currentStep} de {totalSteps}
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground transition hover:bg-accent hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
      <div className="container pb-3">
        <StepsProgress current={currentStep} total={totalSteps} />
      </div>
    </header>
  );
}

function Footer({
  isFirstStep,
  isLastStep,
  submitting,
  submitLabel,
  onBack,
  onNext,
  onSubmit,
}: {
  isFirstStep: boolean;
  isLastStep: boolean;
  submitting: boolean;
  submitLabel: string;
  onBack: () => void;
  onNext: () => void;
  onSubmit: () => void;
}) {
  return (
    <footer className="fixed inset-x-0 bottom-0 z-20 border-t bg-background/95 backdrop-blur md:static md:border-t-0 md:bg-transparent md:backdrop-blur-none">
      <div className="container mx-auto flex max-w-xl items-center justify-between gap-3 px-4 py-3 md:py-8">
        <Button
          type="button"
          variant="ghost"
          onClick={onBack}
          disabled={isFirstStep || submitting}
          className="h-12"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Voltar
        </Button>

        {isLastStep ? (
          <Button
            type="button"
            onClick={onSubmit}
            disabled={submitting}
            className="h-12 flex-1 text-base font-semibold md:flex-none md:px-8"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Analisando…
              </>
            ) : (
              <>
                {submitLabel}
                <Sparkles className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        ) : (
          <Button
            type="button"
            onClick={onNext}
            className="h-12 flex-1 text-base font-semibold md:flex-none md:px-8"
          >
            Continuar
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        )}
      </div>
    </footer>
  );
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

/** Drops `answers` and `_honeypot` from a form value to get the lead-only slice. */
function stripAnswers(v: FormShape): Record<string, unknown> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { answers, _honeypot, ...lead } = v as FormShape & { _honeypot?: string };
  return lead;
}

/**
 * Procura o índice da primeira step que contém um campo com erro.
 * Usa o errors object do react-hook-form e os fieldPath de cada FormField.
 * Retorna -1 se nenhuma step tem erro (caso degenerado).
 */
function findFirstStepWithError(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: any,
  config: FormConfig,
): number {
  if (!errors || typeof errors !== 'object') return -1;
  for (let i = 0; i < config.steps.length; i++) {
    const step = config.steps[i]!;
    for (const f of step.fields) {
      // Resolve o path no errors object · presença = erro nesse field
      const parts = fieldPath(f).split('.');
      let cursor: unknown = errors;
      let found = false;
      for (const part of parts) {
        if (!cursor || typeof cursor !== 'object') break;
        cursor = (cursor as Record<string, unknown>)[part];
        if (cursor !== undefined) found = true;
      }
      if (found && cursor) return i;
    }
  }
  return -1;
}

/** Resolves a dotted error path against react-hook-form's nested errors object. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function readError(errors: any, path: string): string | undefined {
  if (!errors) return undefined;
  const parts = path.split('.');
  let cursor: unknown = errors;
  for (const part of parts) {
    if (!cursor || typeof cursor !== 'object') return undefined;
    cursor = (cursor as Record<string, unknown>)[part];
  }
  if (cursor && typeof cursor === 'object' && 'message' in cursor) {
    const m = (cursor as { message?: unknown }).message;
    return typeof m === 'string' ? m : undefined;
  }
  return undefined;
}

/**
 * Inline copy of the per-field Zod builder. Kept here (instead of imported
 * from schemas) so the form engine on the client can adjust constraints in
 * the future (e.g., relax phone when a non-BR vertical lands) without
 * forking the schemas package.
 */
function buildSingleFieldSchema(field: FormField): z.ZodTypeAny {
  const v = field.validation ?? {};
  switch (field.type) {
    case 'text': {
      let s = z.string().trim();
      if (v.minLength) s = s.min(v.minLength);
      if (v.maxLength) s = s.max(v.maxLength);
      if (v.required ?? true) s = s.min(1, 'Campo obrigatório') as never as typeof s;
      return s;
    }
    case 'email':
      return z.string().trim().toLowerCase().email('E-mail inválido');
    case 'phone':
      // Mesma regra do servidor (DDD válido, 9 no celular): o erro aparece no
      // passo 1, não depois de 4 passos.
      return z
        .string()
        .trim()
        .min(8, 'Telefone incompleto')
        .max(20)
        .refine((v) => normalizeBrazilianPhone(v) !== null, 'Confira o DDD e o número');
    case 'number':
    case 'stepper': {
      let s = z.number().int();
      if (v.min !== undefined) s = s.min(v.min);
      if (v.max !== undefined) s = s.max(v.max);
      return s;
    }
    case 'money-bracket':
      return z.union([z.number().int().nonnegative(), z.null()]);
    case 'single-choice':
      return z.union([z.string(), z.number(), z.boolean()]);
    case 'yes-no':
      return z.boolean();
  }
}
